import React, { memo, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { Button, Checkbox, Form, Input } from 'antd';
import { useDispatch } from 'react-redux';
import { useRequest } from 'ahooks';
import { ConUserWebController } from '@/services/swagger/ConUserWebController';
import { isEmail } from '@/constants/regexp';
import styles from './index.module.scss';

const userWebController = new ConUserWebController();

const typeMap = {
  // 非会员且未留资
  default: {
    icon: 'https://assets-oss.cbndata.com/cbndata-refactor-fe/FtaY0ufhhNoxlPbFMeQoxOJKi8uK.svg',
    title: '订阅品牌',
    text: '提供邮箱后，可订阅5个品牌，我们会在品牌数据更新时给您发送邮件告知',
  },
  // 是会员但未留资
  warning: {
    icon: 'https://assets-oss.cbndata.com/cbndata-refactor-fe/Frw1tXAxV1MedLkLUKupqSLp19th.svg',
    title: '订阅数已满',
    text: '当前订阅数已满，添加以下信息获得更多',
  },
  // 是会员且已留资，且订阅数已满
  error: {
    icon: 'https://assets-oss.cbndata.com/cbndata-refactor-fe/Frw1tXAxV1MedLkLUKupqSLp19th.svg',
    title: '订阅数已满',
    text: '当前订阅数已满，开通中国新消费品牌数据库会员可订阅更多品牌',
  },
  // 提交邮箱后显示成功
  success: {
    icon: 'https://assets-oss.cbndata.com/cbndata-refactor-fe/FmI2BHcoQpS_96-9gUnXjxbHm-Q6.svg',
    title: '邮箱提交成功',
    text: (
      <span>
        当您订阅的品牌数据更新后，我们将通过邮件形式通
        <br />
        知您，去订阅你心仪的品牌吧
      </span>
    ),
  },
};
export type SubscribeBrandModalContentProps = {
  /** 订阅品牌Modal框样式类型 */
  type?: 'default' | 'warning' | 'error' | 'success';
  /** 邮箱留资成功回调 */
  onSubmitEmailSuccess?: () => void;
};
type SubscribeBrandModalFormValueType = {
  email: string;
  checked: boolean;
};
const SubscribeBrandModalContent: React.FC<SubscribeBrandModalContentProps> = ({
  type = 'default',
  onSubmitEmailSuccess,
}) => {
  const [form] = Form.useForm<{ email: string; checked: boolean }>();
  const dispatch = useDispatch();
  const { run } = useRequest(
    async email => {
      const res = await userWebController.conUserWebControllerV1LiuZiUsingPost1({ email });
      return res.data;
    },
    {
      manual: true,
      onSuccess(res) {
        if (res.code === 200) {
          onSubmitEmailSuccess?.();
          dispatch({
            type: 'modal/open',
            payload: { content: <SubscribeBrandModalContent type="success" /> },
          });
        }
      },
    }
  );
  const checked = Form.useWatch('checked', form);
  const iconUrl = useMemo(() => typeMap[type].icon, [type]);
  const title = useMemo(() => typeMap[type].title, [type]);
  const text = useMemo(() => typeMap[type].text, [type]);
  const onClick = useCallback(() => {
    dispatch({ type: 'modal/close' });
  }, [dispatch]);
  const onFinish = useCallback(
    (formValue: SubscribeBrandModalFormValueType) => {
      const { email } = formValue;
      run(email);
    },
    [run]
  );
  return (
    <section className={styles.content}>
      <img src={iconUrl} alt={type} />
      <h1 className={styles.title}>{title}</h1>
      <span className={styles.text}>{text}</span>
      {type === 'error' && (
        <Link href="/member">
          <Button className={styles.navToMemberBtn} type="primary" size="large" onClick={onClick}>
            去了解
          </Button>
        </Link>
      )}
      {type !== 'success' && type !== 'error' && (
        <Form<SubscribeBrandModalFormValueType> className={styles.form} form={form} size="large" onFinish={onFinish}>
          <Form.Item
            label="邮箱"
            name="email"
            rules={[
              { required: true, message: '请填写邮箱' },
              { pattern: isEmail, message: '请填写正确的邮箱' },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item shouldUpdate>
            {() => {
              return (
                <Button
                  htmlType="submit"
                  className={styles.submitBtn}
                  type="primary"
                  block
                  disabled={
                    !form.isFieldsTouched(true) ||
                    !!form.getFieldsError().filter(({ errors }) => errors.length).length ||
                    !checked
                  }
                >
                  提交
                </Button>
              );
            }}
          </Form.Item>
          <Form.Item className={styles.checkboxFormItem} name="checked" valuePropName="checked">
            <Checkbox className={styles.checkbox}>我同意邮件接收内容推送</Checkbox>
          </Form.Item>
        </Form>
      )}
    </section>
  );
};

export default memo(SubscribeBrandModalContent);
